<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card class="elevation-12">
          <v-toolbar color="primary" dark flat>
            <v-toolbar-title>Login</v-toolbar-title>
          </v-toolbar>
          
          <v-card-text>
            <v-btn
              v-if="authStore.oidcLoginEnabled"
              color="primary"
              size="large"
              block
              prepend-icon="mdi-login"
              :loading="oidcLoading"
              class="mb-4"
              @click="signInWithKeycloak"
            >
              Sign in with Keycloak
            </v-btn>

            <div v-if="authStore.oidcLoginEnabled && authStore.nativeLoginEnabled" class="mb-5">
              <v-divider />
              <div class="text-center text-caption text-medium-emphasis mt-2">
                Or use the local break-glass account
              </div>
            </div>

            <v-alert
              v-if="!authStore.oidcLoginEnabled && !authStore.nativeLoginEnabled"
              type="warning"
              variant="tonal"
            >
              No login method is currently configured.
            </v-alert>

            <v-form
              v-if="authStore.nativeLoginEnabled"
              ref="form"
              v-model="valid"
              lazy-validation
              @submit.prevent="submit"
            >
              <v-text-field
                v-model="username"
                :rules="usernameRules"
                label="Username"
                prepend-icon="mdi-account"
                required
                @keyup.enter="submit"
              ></v-text-field>
              
              <v-text-field
                v-model="password"
                :rules="passwordRules"
                label="Password"
                prepend-icon="mdi-lock"
                :type="showPassword ? 'text' : 'password'"
                :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="showPassword = !showPassword"
                required
                @keyup.enter="submit"
              ></v-text-field>
            </v-form>
          </v-card-text>
          
          <v-card-actions v-if="authStore.nativeLoginEnabled">
            <v-btn
              v-if="authStore.registrationEnabled"
              text
              color="primary"
              @click="$router.push('/register')"
            >
              Create Account
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :disabled="!valid || loading"
              :loading="loading"
              @click="submit"
            >
              Login
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    
    const valid = ref(false)
    const loading = ref(false)
    const oidcLoading = ref(false)
    const username = ref('')
    const password = ref('')
    const showPassword = ref(false)
    const snackbar = ref(false)
    const snackbarText = ref('')
    const snackbarColor = ref('error')
    
    const usernameRules = [
      v => !!v || 'Username is required',
      v => v.length >= 3 || 'Username must be at least 3 characters'
    ]
    
    const passwordRules = [
      v => !!v || 'Password is required',
      v => v.length >= 6 || 'Password must be at least 6 characters'
    ]
    
    const showMessage = (message, color = 'error') => {
      snackbarText.value = message
      snackbarColor.value = color
      snackbar.value = true
    }
    
    const submit = async () => {
      if (!valid.value) return
      
      loading.value = true
      
      try {
        const result = await authStore.login({
          username: username.value,
          password: password.value
        })
        
        if (result.success) {
          router.push('/lists')
        } else {
          showMessage(result.error)
        }
      } catch (error) {
        showMessage('An error occurred. Please try again.')
      } finally {
        loading.value = false
      }
    }

    const signInWithKeycloak = async () => {
      oidcLoading.value = true
      try {
        await authStore.startOidcLogin()
      } catch (error) {
        showMessage(error.response?.data?.detail || error.message || 'Keycloak login could not be started.')
        oidcLoading.value = false
      }
    }

    onMounted(() => {
      if (route.query.oidc_error) showMessage(String(route.query.oidc_error))
    })
    
    return {
      valid,
      loading,
      oidcLoading,
      authStore,
      username,
      password,
      showPassword,
      usernameRules,
      passwordRules,
      snackbar,
      snackbarText,
      snackbarColor,
      submit,
      signInWithKeycloak
    }
  }
}
</script>
